import { makeAutoObservable, reaction, runInAction } from "mobx";
import type { ApiClient } from "@/clients/ApiClient";
import type { FeedEntry, FeedResponse, FeedSummary } from "@/generated/api";

type FeedApi = Pick<ApiClient, "get">;

interface FeedAuth {
    readonly status: "unknown" | "anonymous" | "authenticated";
    readonly isSignedIn: boolean;
}

interface FeedFollows {
    readonly podcasts: ReadonlyArray<{ id: number }>;
    readonly loaded: boolean;
}

/** How many episodes one request asks for */
const PAGE_SIZE = 10;

/**
 * What is new from the podcasts the signed-in listener follows (`/me/feed`, the last 30 days, played
 * episodes left out), and how many unplayed new episodes each followed podcast has, for badges.
 * It is read again when the list of followed podcasts changes, so following a podcast shows its new
 * episodes at once. Nothing here is kept on the device: signed out there is no feed.
 */
export class FeedStore {
    entries: FeedEntry[] = [];
    /** Unplayed new episodes per followed podcast id */
    counts: Record<number, number> = {};
    total = 0;
    /** Where the next page starts; empty on the last page */
    nextCursor = "";
    /** True once the first page of the signed-in user has been fetched */
    loaded = false;
    loadingMore = false;

    private generation = 0;

    constructor(
        private readonly client: FeedApi,
        private readonly auth: FeedAuth,
        private readonly follows: FeedFollows
    ) {
        makeAutoObservable<FeedStore, "client" | "auth" | "follows" | "generation">(this, {
            client: false,
            auth: false,
            follows: false,
            generation: false,
        });

        reaction(
            () => this.auth.status,
            (status) => {
                this.reset();
                if (status === "authenticated") void this.load();
            },
            { fireImmediately: true }
        );

        // Following or unfollowing changes what the feed holds; the first read of the follows does not
        let known: string | null = null;
        reaction(
            () => (this.follows.loaded ? this.follows.podcasts.map((podcast) => podcast.id).join(",") : null),
            (key) => {
                const changed = known !== null && key !== null && key !== known;
                known = key;
                if (changed && this.auth.isSignedIn) void this.load();
            }
        );
    }

    /** Unplayed new episodes of a podcast, 0 for none or a podcast that is not followed */
    newCount(podcastId: number): number {
        return this.counts[podcastId] ?? 0;
    }

    async load() {
        if (!this.auth.isSignedIn) return;

        const generation = this.generation;
        try {
            const [feed, summary] = await Promise.all([
                this.client.get<FeedResponse>(`/me/feed?limit=${PAGE_SIZE}`),
                this.client.get<FeedSummary>("/me/feed/summary"),
            ]);
            runInAction(() => {
                // Signed out, or in as someone else, while the requests were on their way
                if (generation !== this.generation) return;
                this.entries = feed.episodes;
                this.nextCursor = feed.next_cursor;
                this.counts = Object.fromEntries(summary.podcasts.map((p) => [p.podcast_id, p.new_count]));
                this.total = summary.total;
                this.loaded = true;
            });
        } catch (error) {
            // Not fatal: the home page simply has no feed section until the next visit
            console.warn("Could not load the feed", error);
        }
    }

    /** The next page, added after what is there. Does nothing on the last page. */
    async loadMore() {
        if (!this.auth.isSignedIn || this.loadingMore || this.nextCursor === "") return;

        const generation = this.generation;
        this.loadingMore = true;
        try {
            const feed = await this.client.get<FeedResponse>(
                `/me/feed?limit=${PAGE_SIZE}&cursor=${encodeURIComponent(this.nextCursor)}`
            );
            runInAction(() => {
                if (generation !== this.generation) return;
                const have = new Set(this.entries.map((entry) => entry.id));
                this.entries.push(...feed.episodes.filter((entry) => !have.has(entry.id)));
                this.nextCursor = feed.next_cursor;
            });
        } catch (error) {
            console.warn("Could not load more of the feed", error);
        } finally {
            runInAction(() => {
                if (generation === this.generation) this.loadingMore = false;
            });
        }
    }

    private reset() {
        this.generation++;
        this.entries = [];
        this.counts = {};
        this.total = 0;
        this.nextCursor = "";
        this.loaded = false;
        this.loadingMore = false;
    }
}
